import Axios from 'axios';
import { Role } from 'models/Role';
import { FeatureFlag, OrganizationPricingPlan } from './organizationApi';
import { Job } from './jobApi';

// Mirrors the server's UserFeatureOverride enum (user-feature-override.enum.ts).
// A per-user permission exception, distinct from FeatureFlag (an org-level toggle).
export enum UserFeatureOverride {
  ORG_JOBS_BY_STATUS_VIEW = 'ORG_JOBS_BY_STATUS_VIEW',
  ORG_NEEDS_ATTENTION_VIEW = 'ORG_NEEDS_ATTENTION_VIEW',
}

export interface User {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  id?: string;
  token?: string;
  organizationId?: string;
  role?: Role;
  dp?: string;
  isOrganizationOwner?: boolean;
  __organization__?: {
    id: number;
    name: string;
    pricingPlan: OrganizationPricingPlan;
    identifier?: string; // Unique identifier for the organization
    featureFlags?: FeatureFlag[]; // List of feature flags enabled for the organization
    earlyAccessFeatureFlags?: FeatureFlag[]; // List of feature flags enabled for early access
  };
  featureOverrides?: UserFeatureOverride[]; // Enabled UserFeatureOverride keys for this user specifically, independent of the org's feature flags
  overlaps?: boolean;
  isLimitedUser?: boolean; // Indicates if the user is an limited user
  ERR_CODE?: string;
  // organizationIdentifier?: string; // Unique identifier for the organization
  branchId?: string;
  branch?: {
    id: string;
    name: string;
    country: string;
    state: string;
  };
}

export interface LimitedUser extends User {
  organizationIdentifier?: string; // Unique identifier for the organization
  isLimitedUser?: boolean; // Indicates if the user is an limited user
  jobs?: (Job & {
    permittedByUser?: User;
    permittedOn?: string;
    permittedByUserId: string;
  })[];
  limitedUserJobsIds?: string[]; // Job IDs permitted to the limited user
}

export interface Profile {
  address?: string;
  dateOfBirth?: string;
  gender?: string;
  title?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactNumber?: string;
  bloodType?: string;
  medicalConditions?: string[];
  certifications?: string[];
  vaccinations?: string[];
}

export interface UserWithProfile extends User {
  profile?: Profile;
}

export async function listUsers(): Promise<User[]> {
  const res = await Axios.get('/api/v1/users');
  return res.data.payload || [];
}

export async function listLimitedUsers(): Promise<LimitedUser[]> {
  const res = await Axios.get('/api/v1/users/limited');
  return res.data.payload || [];
}

export async function addUser(user: User): Promise<User> {
  const res = await Axios.post('/api/v1/users', user);
  return res.data;
}

export async function updateUser({
  uid,
  isCurrentUser,
  ...rest
}: {
  uid: string;
  isCurrentUser?: boolean;
  basicDetails?: User;
  profileDetails?: Profile;
}): Promise<any> {
  const res = await Axios.put(
    `/api/v1/users/${isCurrentUser ? `me/${uid}` : uid}`,
    rest
  );
  return res.data;
}

export async function deleteUser(userID: string) {
  const res = await Axios.delete(`/api/v1/users/soft-delete/${userID}`);
  return res.data;
}

export interface NewRegistration {
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  password: string;
  captchaToken?: string;
  companyIdentifier?: string;
}
export async function newRegistration(registration: NewRegistration): Promise<{
  success: boolean;
  id: string;
  token: string;
}> {
  const res = await Axios.post('/auth/register', registration);
  return res.data;
}

export async function validateEmailToken(token: string) {
  const res = await Axios.post(`/auth/verify-email`, { token });
  return res.data;
}

export async function resendValidateEmail(email: string) {
  const res = await Axios.post(`/auth/verify/resend-email`, { email });
  return res.data;
}

export async function currentUserDetails(): Promise<User> {
  const res = await Axios.get('/api/v1/users/validate');
  return res.data;
}

export async function login(email: string, password: string): Promise<User> {
  const res = await Axios.post('/auth/login', {
    email,
    password,
  });
  return res.data;
}

export async function forgotPassword(email: string) {
  await Axios.post('/auth/forgot-password', { email });
}

export async function validateResetPasswordToken(token: string) {
  const res = await Axios.get(`/auth/reset-password/${token}`);
  return res.data;
}

export async function resetPassword(token: string, newPassword: string) {
  const res = await Axios.post('/auth/reset-password', {
    token,
    password: newPassword,
  });
  return res.data;
}

export async function getUserWithProfile(
  userId: string,
  isCurrentUser: boolean = false
) {
  const res = await Axios.get(
    `/api/v1/users/${isCurrentUser ? 'my/profile' : userId}`
  );
  return res.data;
}

export async function uploadProfilePicture(userId: string, file: any) {
  const formData = new FormData();
  formData.append('image', file);
  const res = await Axios.post(
    `/api/v1/uploads/profile-picture/${userId}`,
    formData,
    {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    }
  );
  return res.data;
}

export async function getLimitedUser(id: string): Promise<LimitedUser> {
  const res = await Axios.get(`/api/v1/users/limited/${id}`);
  return res.data.payload || {};
}

export async function addNewPermissionToLimitedUser({
  limitedUserId,
  jobIds,
}: {
  limitedUserId: string;
  jobIds: string[];
}): Promise<LimitedUser> {
  const res = await Axios.post(
    `/api/v1/users/limited/${limitedUserId}/permitted-jobs`,
    { jobIds }
  );
  return res.data.payload || {};
}

export async function removeJobPermissionFromLimitedUser({
  limitedUserId,
  jobId,
}: {
  limitedUserId: string;
  jobId: string;
}): Promise<LimitedUser> {
  const res = await Axios.delete(
    `/api/v1/users/limited/${limitedUserId}/permitted-jobs/${jobId}`
  );
  return res.data.payload || {};
}
